import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCompanyId } from '@lognai/plugin-sdk';
import {
  fetchCompanyInsights,
  dismissInsight,
  snoozeInsight,
  type FetchInsightsQuery,
  type InsightsListResponse,
  type SnoozeDays,
} from '../api/insights';
import { aiQueryKeys } from './query-keys';

export const useInsights = (query: FetchInsightsQuery = {}) => {
  // Insights are company-scoped. Platform admins (companyId:null, no accessible
  // companies) have no company context, so the backend returns 403 — skip the
  // fetch entirely rather than fire a request that can only fail.
  const hasCompany = !!getCompanyId();
  return useQuery<InsightsListResponse>({
    queryKey: aiQueryKeys.insightsList(query as Record<string, unknown>),
    queryFn: () => fetchCompanyInsights(query),
    enabled: hasCompany,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 20_000,
  });
};

export const useDismissInsight = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => dismissInsight(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insights', 'list'] });
    },
  });
};

/** "Remind me later" — defers the insight for the current user only. */
export const useSnoozeInsight = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, days }: { id: string; days: SnoozeDays }) => snoozeInsight(id, days),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insights', 'list'] });
    },
  });
};
